/**
 * Six materials, and everything in the world is one of them.
 *
 * The old system gave every solid role its own colour out of a per-map
 * palette, put a grid texture on all of them, and then shifted each individual
 * solid's hue a few percent so a merged wall would not read as one flat plane.
 * Every one of those decisions is defensible on its own and together they
 * produced a frame that was 90% weakly-coloured and 8% neutral: nothing was
 * white, nothing was a colour anybody chose, and no surface was more important
 * than any other.
 *
 * The replacement is a fixed vocabulary. Structure is off-white porcelain.
 * Anything you can use — a door, a machine, a rail, a gun — is graphite. The
 * floor is polished resin. Exactly one accent hue per map lands on exactly a
 * few deliberate planes. Hostile things are the only source of coral, and the
 * only source of iridescence.
 *
 * Colour therefore means something: if it is bright, it matters. That is worth
 * more than a hundred and ten differently tinted concretes.
 */

import * as THREE from 'three';
import type { SolidRole } from 'levelspec';
import { ACCENT_HEX, accentOf, profileFor } from './art.ts';

/** The vocabulary. Nothing in the world may be outside this list. */
export type Family = 'porcelain' | 'secondary' | 'resin' | 'graphite' | 'accent' | 'prism';

/** Hostile. Nothing that is not trying to kill you is allowed near this hue. */
export const CORAL = 0xff3158;

/**
 * Which accent a map gets.
 *
 * Read from the map's art profile, which is a table a person edits — the hash
 * of the map's name that used to decide this was stable, which is not the same
 * thing as being chosen. Maps nobody has directed yet still fall back to the
 * hash, and `isDirected` is how the gates tell the two apart.
 */
export function accentFor(id: string): number {
  return accentOf(id);
}

/** The curated set, for anything that needs to check membership. */
export const ACCENTS = ACCENT_HEX;

/**
 * What every role is made of.
 *
 * Structure and its trim are the shell. Anything the player operates or
 * shoots is graphite, because "dark and machined" is the one cue that reads
 * at any distance and in any light. Floors are their own family so the room
 * has a horizon.
 */
const FAMILY: Record<SolidRole, Family> = {
  static_hard: 'porcelain',
  soft_panel: 'secondary',
  reinforcement_slot: 'graphite',
  glass: 'porcelain',
  window_barricade: 'graphite',
  door_panel: 'graphite',
  lintel: 'secondary',
  sill: 'secondary',
  floor: 'resin',
  /*
   * A soffit, not a lid.
   *
   * This was graphite, and the argument for it was sound as far as it went: a
   * ceiling is the one plane you are never lit by and never walk on, it is
   * where the services live, and a room with a bright one reads as continuing
   * upward for ever.
   *
   * What it did not account for is a map with more than one storey. In an
   * enclosed band you are standing between a dark floor and a dark ceiling
   * with only the walls carrying any light at all, and the walls are a
   * minority of the frame — six of the authored maps came back at between
   * fifty-seven and seventy-seven per cent of the picture below the graphite
   * threshold, which is not a lit interior, it is a power cut. Every traced
   * map in the library is a single open storey and none of them ever showed
   * it.
   *
   * A soffit is a light-reflecting surface in nearly every building that has
   * one, and the graphite an outdoor frame needs now comes from the coping
   * along the top of every wall, which is a better place for it: you can
   * actually see a roofline from the street, and you cannot see a roof.
   */
  roof: 'secondary',
  stair: 'secondary',
  ramp: 'secondary',
  ladder: 'graphite',
  hatch: 'graphite',
  cover: 'graphite',
  /*
   * The vertical face of a step or a kerb, and not nearly as dark as it was.
   *
   * The old reasoning — a level change you cannot see is a level change you
   * walk off — is right about a kerb and catastrophically wrong about a
   * flight, because the compiler does not build flights out of steps. It
   * builds them out of five-and-a-half-centimetre slices, so that the player
   * controller never has to step and walking up is continuous, and *every one
   * of those slices has a riser face*. A four-metre climb is ninety of them.
   *
   * Painted near-black that is not a staircase with dark risers, it is a black
   * wall with faint pale lines in it — six thousand triangles of it on a map
   * with three bands, and it is why every authored map came back with three
   * quarters of the frame below the graphite threshold while every traced one
   * was fine. The traced maps are single-storey and have almost no stairs.
   *
   * A riser in a pale building is the same stone as the tread, in shadow. The
   * graphite an outdoor frame needs comes from the coping now.
   */
  riser: 'secondary',
  exterior_wall: 'porcelain',
};

export function familyOf(role: SolidRole): Family {
  return FAMILY[role] ?? 'porcelain';
}

/**
 * A cube map built from the sky, so smooth things have something to reflect.
 *
 * Most of what makes a polished floor look polished is not a mirror, it is a
 * broad gradient sliding across it as you turn. One small generated
 * environment gives every material in the game that, at a cost paid once.
 */
export function skyEnvironment(
  renderer: THREE.WebGLRenderer,
  top: number,
  horizon: number,
  ground: number,
): THREE.Texture {
  const scene = new THREE.Scene();
  const geo = new THREE.SphereGeometry(1, 16, 12);
  const mat = new THREE.ShaderMaterial({
    side: THREE.BackSide,
    uniforms: {
      top: { value: new THREE.Color(top) },
      horizon: { value: new THREE.Color(horizon) },
      ground: { value: new THREE.Color(ground) },
    },
    vertexShader: `
      varying vec3 vDir;
      void main() {
        vDir = normalize(position);
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }`,
    fragmentShader: `
      uniform vec3 top; uniform vec3 horizon; uniform vec3 ground;
      varying vec3 vDir;
      void main() {
        float y = vDir.y;
        vec3 c = y > 0.0 ? mix(horizon, top, pow(y, 0.55)) : mix(horizon, ground, pow(-y, 0.7));
        gl_FragColor = vec4(c, 1.0);
      }`,
  });
  scene.add(new THREE.Mesh(geo, mat));
  const pmrem = new THREE.PMREMGenerator(renderer);
  const target = pmrem.fromScene(scene, 0.04);
  geo.dispose();
  mat.dispose();
  pmrem.dispose();
  /*
   * The target as well as its texture.
   *
   * `fromScene` hands back a render target, and the caller only ever wants the
   * texture off it — so the framebuffer behind it had no owner and no way to
   * be freed. `scene.environment?.dispose()` releases the cubemap, which is
   * the big part, and leaves that. One per map is invisible; a sweep builds
   * two hundred and fifty-five of them in one page.
   *
   * Held here rather than returned, because every caller wants a texture and
   * none of them should have to know a render target was involved.
   */
  previous?.dispose();
  previous = target;
  return target.texture;
}

/** The last environment built, so the next one can free it. */
let previous: THREE.WebGLRenderTarget | undefined;

/**
 * Which side of a floor slab is being looked at, in effect.
 *
 * `sky` is ground with nothing over it; `ceiling` is a slab with a room under
 * it, so it is somebody's ceiling as well as somebody's floor; `inside` is an
 * ordinary interior floor with neither.
 */
export type FloorKind = 'inside' | 'sky' | 'ceiling';

export interface Surfaces {
  /**
   * The material for a role.
   *
   * `outdoor` matters for one role and one reason. A floor indoors is polished
   * resin; the ground outside is hardstanding, and hardstanding is dark. On a
   * map with no roof and no interior — a yard, a rooftop, a traced draft where
   * every space is still exterior — the ground is the largest surface in every
   * frame, and if it is pale then the entire map is pale and the composition
   * has no value range at all. Nothing else in the vocabulary can supply that,
   * because a map like this has no ceilings, no doors and no machinery for the
   * graphite to be on.
   */
  get(role: SolidRole, kind?: FloorKind): THREE.Material;
  /** The map's one accent, for anything that wants to match it. */
  readonly accent: number;
  dispose(): void;
}

/**
 * The materials for one floor.
 *
 * One instance per role that is actually used, made once and shared by every
 * solid with that role — which is also why the per-solid tint had to go: there
 * is no per-solid anything any more.
 */
export function surfacesFor(mapId: string): Surfaces {
  const accent = accentFor(mapId);
  const made = new Map<string, THREE.Material>();

  const build = (role: SolidRole, kind: FloorKind): THREE.Material => {
    if (role === 'floor' && kind === 'ceiling') {
      /*
       * A slab is painted once and seen twice.
       *
       * The floor of an upper storey is the ceiling of the one below it, and
       * the two want opposite things. Hardstanding — the dark ground the
       * outdoor test used to hand it — is the darkest thing in the palette,
       * and overhead in a room lit only by ambient it took an exchange's
       * trading floor to 94% dark. Resin, the indoor floor, is mid, and giving
       * the ceiling that instead took the dark mass out of five other rooms
       * and failed them for having no range at all. Six fixed, five broken.
       *
       * So it is neither: a third value, dark enough to read as the frame's
       * dark mass and light enough that a room under one is still a room.
       * Above it is somebody's floor, which is why it is not darker still.
       *
       * Found by sweeping, on the same 24 maps, counting maps clean on every
       * view out of 24:
       *
       *   0x262c33  hardstanding   5    subject 14  tinted  6  masses 5
       *   0x8b949d  resin          6    subject  6  tinted 11  masses 9
       *   0x4d555e                 8    subject 11  tinted  8  masses 5
       *   0x5a636d                 2    subject 11  tinted 15  masses 9
       *   0x50555b                 9    subject 11  tinted  6  masses 5
       *
       * The fourth and fifth rows are the same lightness and differ only in
       * chroma — 19 against 11 — and that alone is seven maps, because a
       * ceiling is a large flat area and a large flat area is where `tinted`
       * gets counted. Whatever value this lands on, it wants to be grey.
       *
       * And then it moved once more, up to 0.383, because the reason the
       * lighter row lost had been fixed in the meantime: every neutral family
       * had its chroma halved, so a light ceiling no longer spends the whole
       * `tinted` budget. What it was costing instead was the frames that fail
       * for being too *dark* — a radar station is 40% of one frame this slab,
       * walked across rather than looked up at, and read 67.3% dark against a
       * bar of 52. At 0.383 that is 61.4%, a pier 62.5% to 56.2%, a blockhouse
       * 61% to 52.8%, a bunkering 60.8% to 52.7%, and another blockhouse 63.2%
       * to 32.0% — with nothing on the pale side of the library moving against
       * it, and a cabletop's dark mass improving from 4.2% to 7.7%.
       *
       * 0.441 is too far. The dark frames keep improving, and the pale ones
       * start paying for it: a refuge goes from 53.9% pale to 73.1%, a cistern
       * 31.5% to 41.1%, a quarry 60.4% to 65.6%, and a data hall that was
       * clean stops being. The floor is the frame's dark mass now, so the
       * ceiling can come back toward the walls — but only until the rooms that
       * had no dark mass to begin with start losing what little they have.
       */
      /*
       * And it splits by rig, for the same reason the ground does.
       *
       * Lightening this from 0.331 to 0.383 was for the frames that fail for
       * being too dark, and it worked — but it took the dark mass out from
       * under a different set, and the library's `masses` failures of the pale
       * kind went from 13 to 25 across the work that followed.
       *
       * The two sets are cleanly separated by lighting, which is what makes a
       * split honest rather than convenient: every frame that fails for being
       * too dark is on `blue_hour` or `clinical_night`, and every one that
       * fails for having no dark mass is on `cool_overcast`, `deep_interior`
       * or `warm_interior`. A dim building needs its ceiling up because
       * nothing else in the frame is lit; a bright one can afford it dark
       * because everything else is.
       */
      const dimRig = profileFor(mapId).lighting;
      const overhead = dimRig === 'blue_hour' || dimRig === 'clinical_night' ? 0x5f6265 : 0x50555b;
      return new THREE.MeshStandardMaterial({
        vertexColors: true, color: overhead, roughness: 0.42, metalness: 0.04, envMapIntensity: 0.7,
      });
    }
    if (role === 'floor' && kind === 'sky') {
      /*
       * Matte, and genuinely dark: rain and traffic, rather than a lobby.
       *
       * Dark enough to survive being lit. A mid grey outdoors reads as mid
       * grey in a foundry at dusk and as very nearly white under a bright sky,
       * which puts the largest surface in the frame on the wrong side of the
       * value range on exactly the maps that most need something to hold the
       * bottom of it.
       *
       * Up from 0.170 to 0.217, and its chroma down from 0.051 — the highest
       * of any neutral, which is what is left of a halving the others got and
       * this one missed. The frames that fail hardest for being too dark are
       * open ones with the road filling half the picture: a calavera 43% of
       * one frame this material, a dizzy 45%, a lunacy 46%. The lift takes
       * dizzy clean and lunacy from 58.5% dark to 57.5%, and nothing on the
       * pale side of the library moves against it.
       *
       * It stops there because 0.253 is where hardstanding and the interior
       * floor become the same value, and "hardstanding is dark, and it is
       * never anybody's ceiling" stops meaning anything. The extra step buys
       * lunacy 1.3 points and no maps.
       *
       * And it does not reach a calavera at all — 58.8% dark to 58.7% for a
       * 49% rise in albedo. Sampling the frame says why: the lower third of it
       * renders between 0.10 and 0.20 whatever this number is, because blue
       * hour is dim and a road at dusk is dark. That frame is not badly made;
       * it is a dusk street with lit buildings over a dark road, and the gate
       * marks it down for having a road in it. Fixing it means either a rule
       * that knows how much light the scene has or a light at ground level,
       * and neither is a number here.
       */
      /*
       * And lighter again where there is barely any light on it.
       *
       * This value is set so the ground survives being lit — dark enough that
       * a bright sky does not wash the largest surface in the frame to nearly
       * white. Under a dim one there is nothing to survive: sampling a dusk
       * frame gives the lower third of it between 0.10 and 0.20, which is a
       * road you cannot read the shape of. The argument is legibility of the
       * thing you are walking on rather than anything about the gates, and it
       * only applies where the sky has gone, so the bright maps this number
       * was chosen for keep it.
       */
      /*
       * Two values, because dusk and night are not the same problem.
       *
       * By the rig's name and not by its ambient: `hard_morning` has the
       * lowest fill in the set and a key at 2.15, which is a bright morning
       * and not a dim anything, and my first version of this test caught it.
       *
       * Blue hour wants the ground well up. It is the frames where the road is
       * half the picture and everything else in them is lit, so a dark road is
       * the only mass in the shot and the shot is nothing but road. At 0.413 a
       * lunacy comes clean on all twelve views, and a chinatown, a motel and a
       * dogtown lose their dark failures with it.
       *
       * Night wants it left where it was. Those maps have the opposite problem
       * already — they fail for having too little *pale*, not too much dark —
       * and the same 0.413 takes the dark mass out from under them: an
       * extraction to 6.7% dark, a nuke to 2.4%, a phoenix compound to 3.2%,
       * and the twelve of them from one clean to none. The ground is the only
       * dark thing left in a night frame and it has to stay dark.
       */
      const dusk = profileFor(mapId).lighting === 'blue_hour';
      return new THREE.MeshStandardMaterial({
        vertexColors: true,
        color: dusk ? 0x656a70 : 0x33383e,
        roughness: 0.74, metalness: 0.05, envMapIntensity: 0.3,
      });
    }
    /*
     * The neutrals are grey, and it takes some doing.
     *
     * `tinted` counts pixels whose chroma is between a tenth and three tenths
     * — the band where nothing is a colour anybody chose and the frame is
     * quietly stained — and forty maps of the library were failing it, whole
     * concepts at a time, which is the signature of a palette rather than of a
     * floorplan. A funicular station came out 40.2% stained looking like a
     * perfectly ordinary grey station.
     *
     * The cause is multiplication. A surface with a little chroma lit by a sky
     * with a little chroma has the sum of the two, and the neutrals and the
     * skies were each cool enough on their own to be unremarkable:
     *
     *                own chroma   under a 0.067 sky
     *   porcelain      0.043           0.098
     *   secondary      0.075           0.116
     *   resin          0.071           0.102
     *
     * Every one of them at or over the line, from two greys that each looked
     * grey. So each family's chroma is halved and its luminance held to within
     * half a step of 255, which changes nothing anybody can see — the station
     * is the same station, luminance 0.450 against 0.451 — and takes it from
     * 40.2% stained to 6.2%. On the 24-map sample `tinted` went from eight
     * maps to none and the clean count from nine to sixteen.
     */
    const family = familyOf(role);
    // No `envMap` per material: the scene carries one and three hands it to
    // every standard material, which is one texture rather than nineteen
    // references to it and one place to change when the sky does.
    const common = { vertexColors: true } as const;
    switch (family) {
      case 'resin':
        /*
         * Dark, and polished. Both halves matter, and the first one was wrong.
         *
         * A pale floor under pale walls under a pale ceiling is a frame with
         * no value range in it at all, and the sweep says so plainly: on six
         * of the hand-traced maps the worst view came out a hundred per cent
         * pale with no dark mass anywhere. Every one of those is a room the
         * player will stand in.
         *
         * The outdoor ground already knew this and is 0x262c33. Nothing about
         * "resin" ever meant *light* — it means polished, and a polished dark
         * floor is both the more convincing material and the thing that holds
         * the bottom of the range in every interior frame. It is also where a
         * dark mass belongs architecturally: underfoot, unavoidably in shot,
         * rather than a black box parked in the corner to satisfy a number.
         *
         * Polished, not mirrored. The reflection is a broad environment
         * gradient; a true mirror floor doubles every mistake in the room.
         *
         * And dark after all, which is the fourth time this value has moved
         * and the first time the reason has gone away rather than been
         * outweighed.
         *
         * It was made mid because a floor slab is also the *ceiling* of the
         * band below it, so on a section map it is twice as much of the frame
         * as it is on a single storey, and a room whose floor, ceiling and
         * machinery all count as dark has only its walls left to be pale with.
         * That was true, and it is not true any more: a slab with a room under
         * it is now its own material, and `resin` only ever paints a floor
         * that is nobody's ceiling. The thing it was compromising for no
         * longer shares the value with it.
         *
         * So it goes back to where the argument always pointed — "where a dark
         * mass belongs architecturally: underfoot, unavoidably in shot" — and
         * the frames that had no dark mass at all get one. A silo's worst view
         * was 72.0% pale against 5.4% dark, an eighth of the eight per cent
         * `masses` asks for, with 44% of the frame a staircase and 22% of it
         * this floor; it comes out 43.8% and 23.3%, spread 0.69, and the map
         * goes from failing to clean on every view.
         *
         * Measured on the twenty maps that were failing `masses` plus six that
         * were clean: 6 of 26 clean to 14, no control regressed. Over the
         * whole library the frames with no dark mass in them go from 22 to 13.
         *
         * It costs something, and the cost is on the maps that were already
         * dark: a pier went from 35.9% dark to 74.1%, and `masses` failures of
         * the *other* kind — too little pale — rose while the ones this is for
         * fell. Net, the library gains six maps clean on every view.
         *
         * Halfway is not better. At 0x50555b — the value the ceiling sweep
         * settled on, luminance 0.331 — the silo goes back to failing at
         * 55.5% pale against 6.3% dark, and the too-dark maps are barely moved
         * (a pier 74.1% to 71.2%, still failing). They are not failing because
         * of the floor, so meeting them halfway gives up a map and buys
         * nothing.
         *
         * What this does not fix is a frame with no floor in it — a back alley
         * looking into the corner of two walls is 96.2% pale whatever the
         * floor is made of.
         *
         * The ground *outside* is a different material again and stays where
         * it was: hardstanding is darker still, and it is never anybody's
         * ceiling either.
         *
         * I moved it to 0x272b30 and moved it back. The argument was good —
         * on a yard the ground is most of the frame, and at 0x33383e it sits
         * just above the dark band once the light reaches it, so the largest
         * surface in shot is not the dark mass it was drawn to be — and the
         * measurement I took to support it was on the wrong sample. Twenty
         * maps that were failing `masses` and thirty that were clean: two of
         * the twenty came good and none of the thirty moved, which looked
         * conclusive. It was not: the whole library says 206 clean before and
         * 204 after. It fixes a siege and an engage and breaks a militia, a
         * mint, a dizzy and a rialto, none of which were in the control.
         *
         * A control has to be able to fail. Thirty maps chosen from the same
         * rig as the maps being fixed cannot show you what a change costs on
         * the other five.
         *
         * The same darkening applied to *this* value buys nothing and costs
         * two maps, which is the fourth time that has been true. The frames
         * still failing are not failing because of the floor.
         */
        return new THREE.MeshStandardMaterial({
          ...common, color: 0x3f444a, roughness: 0.2, metalness: 0.02, envMapIntensity: 1.15,
        });
      case 'graphite':
        return new THREE.MeshStandardMaterial({
          ...common, color: 0x151a20, roughness: 0.34, metalness: 0.55, envMapIntensity: 0.7,
        });
      case 'secondary':
        return new THREE.MeshStandardMaterial({
          ...common, color: 0xb4b9bd, roughness: 0.68, metalness: 0.0, envMapIntensity: 0.5,
        });
      case 'accent':
        return new THREE.MeshStandardMaterial({
          ...common, color: accent, roughness: 0.4, metalness: 0.0, envMapIntensity: 0.45,
        });
      default:
        if (role === 'glass') {
          return new THREE.MeshStandardMaterial({
            ...common, color: 0xdfe6ea, roughness: 0.08, metalness: 0.0, envMapIntensity: 1.2,
            transparent: true, opacity: 0.24, depthWrite: false, side: THREE.DoubleSide,
          });
        }
        /*
         * The bottom of the porcelain range rather than the top.
         *
         * At 0xe7eaeb a lit face came out of the tone map within a whisker of
         * white, and on a bright daylight map two thirds of the frame was
         * bunched at the top of the range with no tonal separation left in it
         * — which is the exact failure a single-key rig on white architecture
         * is supposed to avoid. Four per cent darker is invisible as a colour
         * and is the difference between a lit wall and a clipped one.
         */
        return new THREE.MeshStandardMaterial({
          ...common, color: 0xdfe2e5, roughness: 0.62, metalness: 0.0, envMapIntensity: 0.55,
        });
    }
  };

  return {
    accent,
    get(role: SolidRole, kind: FloorKind = 'inside'): THREE.Material {
      const key = `${kind}:${role}`;
      let m = made.get(key);
      if (!m) made.set(key, (m = build(role, kind)));
      return m;
    },
    dispose(): void {
      for (const m of made.values()) m.dispose();
      made.clear();
    },
  };
}
